import ArrowRightIcon from "@redotech/redo-icons/arrow-right_filled.svg";
import {
  CarrierAccountType,
  RedoCarrierName,
  redoCarrierNameToCarrier,
} from "@redotech/redo-model/carrier-account/carrier-account";
import {
  Carriers,
  carrierToLabel,
} from "@redotech/redo-model/fulfillments/fulfillment-carriers-and-services";
import { isEnumValue } from "@redotech/util/enum";
import { assertNever } from "@redotech/util/type";
import { memo, useContext, useMemo } from "react";
import { RedoTextInput } from "../../../arbiter-components/input/redo-text-input";
import { RedoBaseModal } from "../../../arbiter-components/modal/redo-base-modal";
import {
  RedoModalFooter,
  RedoModalHeader,
} from "../../../arbiter-components/modal/redo-modal";
import { TeamContext } from "../../../context/team-context";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import { OptionalTooltip } from "../../../tooltip/optional-tooltip";
import { LogoSize, ShippingLogo } from "../../common/shipping-logo";
import {
  AmazonCarrierForm,
  CreateAmazonCarrierForm,
} from "./carrier-forms/create-amazon-carrier-form";
import {
  ApcCarrierForm,
  CreateApcCarrierForm,
} from "./carrier-forms/create-apc-carrier-form";
import {
  AsendiaCarrierForm,
  CreateAsendiaCarrierForm,
} from "./carrier-forms/create-asendia-carrier-form";
import {
  AustraliaPostCarrierForm,
  CreateAustraliaPostCarrierForm,
} from "./carrier-forms/create-australia-post-carrier-form";
import {
  CanadaPostCarrierForm,
  CreateCanadaPostCarrierForm,
} from "./carrier-forms/create-canada-post-carrier-form";
import {
  CreateDHLCarrierForm,
  DHLCarrierForm,
} from "./carrier-forms/create-dhl-carrier-form";
import {
  CreateDHLExpressCarrierForm,
  DHLExpressCarrierForm,
} from "./carrier-forms/create-dhl-express-carrier-form";
import {
  CreateEPostGlobalCarrierForm,
  EPostGlobalCarrierForm,
} from "./carrier-forms/create-epost-global-carrier-form";
import {
  CreateETAExpressCarrierForm,
  ETAExpressCarrierForm,
} from "./carrier-forms/create-eta-express-carrier-form";
import {
  CreateFedexCarrierForm,
  FedexCarrierForm,
} from "./carrier-forms/create-fedex-carrier-form";
import {
  CreateFedexCrossBorderCarrierForm,
  FedexCrossBorderCarrierForm,
} from "./carrier-forms/create-fedex-international-carrier-form";
import {
  CreateFedexSmartpostCarrierForm,
  FedexSmartpostCarrierForm,
} from "./carrier-forms/create-fedex-smartpost-carrier-form";
import {
  CreateFirstMileCarrierForm,
  FirstMileCarrierForm,
} from "./carrier-forms/create-first-mile-carrier-form";
import {
  CreateGlsCarrierForm,
  GlsCarrierForm,
} from "./carrier-forms/create-gls-carrier-form";
import {
  CreateOnTracCarrierForm,
  OnTracCarrierForm,
} from "./carrier-forms/create-ontrac-carrier-form";
import {
  CreatePassportGlobalCarrierForm,
  PassportGlobalCarrierForm,
} from "./carrier-forms/create-passport-global-carrier-form";
import {
  CreateRoyalMailCarrierForm,
  RoyalMailCarrierForm,
} from "./carrier-forms/create-royal-mail-carrier-form";
import {
  CreateRoyalMailV3CarrierForm,
  RoyalMailV3CarrierForm,
} from "./carrier-forms/create-royal-mail-v3-carrier-form";
import {
  CreateSekoOmniParcelCarrierForm,
  SekoOmniParcelCarrierForm,
} from "./carrier-forms/create-seko-omni-parcel-carrier-form";
import {
  CreateSpeeDeeCarrierForm,
  SpeeDeeCarrierForm,
} from "./carrier-forms/create-spee-dee-carrier-form";
import {
  CreateUPSCarrierForm,
  CreateUPSSurepostCarrierForm,
  UPSCarrierForm,
  UPSSurepostCarrierForm,
} from "./carrier-forms/create-ups-carrier-form";
import {
  CreateUpsMailInnovationsCarrierForm,
  UpsMailInnovationsCarrierForm,
} from "./carrier-forms/create-ups-mail-innovations-carrier-form";
import {
  CreateUSPSCarrierForm,
  USPSCarrierForm,
} from "./carrier-forms/create-usps-carrier-form";
import {
  CreateUspsDirectCarrierForm,
  UspsDirectCarrierForm,
} from "./carrier-forms/create-usps-direct-carrier-form";
import {
  CreateUspsShipCarrierForm,
  UspsShipCarrierForm,
} from "./carrier-forms/create-usps-ship-carrier-form";
import {
  CreateVehoCarrierForm,
  VehoCarrierForm,
} from "./carrier-forms/create-veho-carrier-form";
import { CarrierInputForm, CarrierInputValue } from "./carrier-input";

export const AddCarrierModal = memo(function AddCarrierModal({
  carrierOptions,
  onClose,
  isOpen,
  selectedCarrier,
  setSelectedCarrier,
  newCarrier,
  onAddCarrier,
  carrierAccountType,
  onOpenFedexMfaModal,
  canAddDirectUspsOverride = false,
}: {
  carrierOptions: RedoCarrierName[];
  onClose: () => void;
  isOpen: boolean;
  selectedCarrier?: RedoCarrierName;
  setSelectedCarrier: (
    carrier: React.SetStateAction<RedoCarrierName | undefined>,
  ) => void;
  newCarrier: CarrierInputForm;
  onAddCarrier: (carrier: CarrierInputValue) => Promise<{ success: boolean }>;
  carrierAccountType: CarrierAccountType;
  onOpenFedexMfaModal?: () => void;
  canAddDirectUspsOverride: boolean;
}) {
  const team = useContext(TeamContext);
  // Filter out USPS and USPS Ship from the UI (they still exist in the backend)
  // Keep USPS_DIRECT (now displays as "USPS") - it will show tooltip if setting is off
  const filteredCarrierOptions = useMemo(() => {
    const filteredForOMS = carrierOptions.filter(
      (carrier) =>
        carrier !== RedoCarrierName.USPS &&
        carrier !== RedoCarrierName.USPS_SHIP,
    );
    const filteredForReturns = carrierOptions.filter(
      (carrier) => carrier !== RedoCarrierName.USPS_DIRECT,
    );

    let filtered;
    if (team?.settings?.outboundLabels?.newOmsEnabled) {
      filtered = filteredForOMS;
    } else {
      filtered = filteredForReturns;
    }

    // Sort carriers alphabetically
    return filtered.sort((a, b) => a.localeCompare(b));
  }, [carrierOptions, team?.settings.outboundLabels?.newOmsEnabled]);

  const canSubmit = useMemo(() => {
    const carrierToInput: Record<
      RedoCarrierName,
      | DHLCarrierForm
      | DHLExpressCarrierForm
      | UPSCarrierForm
      | UPSSurepostCarrierForm
      | UpsMailInnovationsCarrierForm
      | USPSCarrierForm
      | FedexCrossBorderCarrierForm
      | FedexCarrierForm
      | FedexSmartpostCarrierForm
      | VehoCarrierForm
      | ApcCarrierForm
      | FirstMileCarrierForm
      | AustraliaPostCarrierForm
      | CanadaPostCarrierForm
      | RoyalMailCarrierForm
      | RoyalMailV3CarrierForm
      | SekoOmniParcelCarrierForm
      | GlsCarrierForm
      | AsendiaCarrierForm
      | SpeeDeeCarrierForm
      | AmazonCarrierForm
      | UspsShipCarrierForm
      | UspsDirectCarrierForm
      | PassportGlobalCarrierForm
      | EPostGlobalCarrierForm
      | ETAExpressCarrierForm
      | OnTracCarrierForm
    > = {
      [RedoCarrierName.DHL_ECS]: newCarrier.inputs.dhlCarrierInput,
      [RedoCarrierName.DHL_EXPRESS]: newCarrier.inputs.dhlExpressCarrierInput,
      [RedoCarrierName.UPS]: newCarrier.inputs.upsCarrierInput,
      [RedoCarrierName.UPS_SUREPOST]: newCarrier.inputs.upsSurepostCarrierInput,
      [RedoCarrierName.UPS_MAIL_INNOVATIONS]:
        newCarrier.inputs.upsMailInnovationsCarrierInput,
      [RedoCarrierName.USPS]: newCarrier.inputs.uspsCarrierInput,
      [RedoCarrierName.FEDEX_CROSS_BORDER]:
        newCarrier.inputs.fedexCrossBorderCarrierInput,
      [RedoCarrierName.FEDEX]: newCarrier.inputs.fedexCarrierInput,
      [RedoCarrierName.FEDEX_SMARTPOST]:
        newCarrier.inputs.fedexSmartpostCarrierInput,
      [RedoCarrierName.VEHO]: newCarrier.inputs.vehoCarrierInput,
      [RedoCarrierName.APC]: newCarrier.inputs.apcCarrierInput,
      [RedoCarrierName.FIRST_MILE]: newCarrier.inputs.firstMileCarrierInput,
      [RedoCarrierName.AUSTRALIA_POST]:
        newCarrier.inputs.australiaPostCarrierInput,
      [RedoCarrierName.CANADA_POST]: newCarrier.inputs.canadaPostCarrierInput,
      [RedoCarrierName.ROYAL_MAIL]: newCarrier.inputs.royalMailCarrierInput,
      [RedoCarrierName.ROYAL_MAIL_V3]:
        newCarrier.inputs.royalMailV3CarrierInput,
      [RedoCarrierName.SEKO_OMNIPARCEL]:
        newCarrier.inputs.sekoOmniParcelCarrierInput,
      [RedoCarrierName.GLS]: newCarrier.inputs.glsCarrierInput,
      [RedoCarrierName.ASENDIA]: newCarrier.inputs.asendiaCarrierInput,
      [RedoCarrierName.SPEE_DEE]: newCarrier.inputs.speeDeeCarrierInput,
      [RedoCarrierName.AMAZON]: newCarrier.inputs.amazonCarrierInput,
      [RedoCarrierName.USPS_SHIP]: newCarrier.inputs.uspsShipCarrierInput,
      [RedoCarrierName.USPS_DIRECT]: newCarrier.inputs.uspsDirectCarrierInput,
      [RedoCarrierName.PASSPORT_GLOBAL]:
        newCarrier.inputs.passportGlobalCarrierInput,
      [RedoCarrierName.EPOST_GLOBAL]: newCarrier.inputs.epostGlobalCarrierInput,
      [RedoCarrierName.ETA_EXPRESS]: newCarrier.inputs.etaExpressCarrierInput,
      [RedoCarrierName.ONTRAC]: newCarrier.inputs.onTracCarrierInput,
    };

    return (
      newCarrier.inputs.name.allErrors.length === 0 &&
      carrierToInput[newCarrier.inputs.redoCarrierName.value].allErrors
        .length === 0
    );
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newCarrier.allErrors, newCarrier.inputs.redoCarrierName.value]);

  let title = "";
  let subtitle = "";

  switch (carrierAccountType) {
    case CarrierAccountType.MERCHANT:
      title = "Bring your own carrier";
      break;
    case CarrierAccountType.REDO_CHILD:
      title = "Add a Redo child carrier account";
      break;
    default:
      assertNever(carrierAccountType);
  }

  switch (carrierAccountType) {
    case CarrierAccountType.MERCHANT:
      subtitle = "Add a supported carrier account to bring your own rates";
      break;
    case CarrierAccountType.REDO_CHILD:
      subtitle =
        "Add a Redo child carrier account to support Redo rates for accounts that have different credentials per merchant";
      break;
    default:
      assertNever(carrierAccountType);
  }

  return (
    <RedoBaseModal isOpen={isOpen} onModalCloseRequested={onClose}>
      <RedoModalHeader
        cancelClicked={onClose}
        subtitle={subtitle}
        title={title}
      />
      <Flex
        bgColor="secondary"
        borderBottomWidth="1px"
        borderColor="primary"
        borderStyle="solid"
        borderTopWidth="1px"
        p="3xl"
      >
        {selectedCarrier ? (
          <AddCarrierForm newCarrier={newCarrier} />
        ) : (
          <Flex gap="3xl" justify="center" wrap="wrap">
            {filteredCarrierOptions.map((carrier) => (
              <CarrierCard
                allowCreate={
                  carrier !== RedoCarrierName.USPS_DIRECT ||
                  canAddDirectUspsOverride
                }
                carrier={carrier}
                key={carrier}
                onClick={(carrier: RedoCarrierName) => {
                  if (
                    carrier === RedoCarrierName.FEDEX &&
                    !!onOpenFedexMfaModal
                  ) {
                    onOpenFedexMfaModal();
                    onClose();
                  } else {
                    setSelectedCarrier(carrier);
                    newCarrier.inputs.redoCarrierName.setValue(carrier);
                  }
                }}
              />
            ))}
          </Flex>
        )}
      </Flex>
      {selectedCarrier && (
        <RedoModalFooter
          primaryButton={{
            onClick: async () => {
              const { success } = await onAddCarrier(newCarrier.value);
              if (success) {
                onClose();
              }
            },
            text: "Add carrier account",
            size: "sm",
            disabled: !canSubmit,
          }}
          secondaryButton={{
            onClick: () => setSelectedCarrier(undefined),
            text: "Back",
            size: "sm",
          }}
        />
      )}
    </RedoBaseModal>
  );
});

const carrierSpecificForm = (newCarrier: CarrierInputForm) => {
  switch (newCarrier.inputs.redoCarrierName.value) {
    case RedoCarrierName.DHL_ECS:
      return (
        <CreateDHLCarrierForm
          carrierInput={newCarrier.inputs.dhlCarrierInput}
        />
      );
    case RedoCarrierName.DHL_EXPRESS:
      return (
        <CreateDHLExpressCarrierForm
          carrierInput={newCarrier.inputs.dhlExpressCarrierInput}
        />
      );
    case RedoCarrierName.UPS:
      return (
        <CreateUPSCarrierForm
          carrierInput={newCarrier.inputs.upsCarrierInput}
        />
      );
    case RedoCarrierName.UPS_SUREPOST:
      return (
        <CreateUPSSurepostCarrierForm
          carrierInput={newCarrier.inputs.upsSurepostCarrierInput}
        />
      );
    case RedoCarrierName.UPS_MAIL_INNOVATIONS:
      return (
        <CreateUpsMailInnovationsCarrierForm
          carrierInput={newCarrier.inputs.upsMailInnovationsCarrierInput}
        />
      );
    case RedoCarrierName.USPS:
      return (
        <CreateUSPSCarrierForm
          carrierInput={newCarrier.inputs.uspsCarrierInput}
        />
      );
    case RedoCarrierName.FEDEX:
      return (
        <CreateFedexCarrierForm
          carrierInput={newCarrier.inputs.fedexCarrierInput}
        />
      );
    case RedoCarrierName.FEDEX_SMARTPOST:
      return (
        <CreateFedexSmartpostCarrierForm
          carrierInput={newCarrier.inputs.fedexSmartpostCarrierInput}
        />
      );
    case RedoCarrierName.FEDEX_CROSS_BORDER:
      return (
        <CreateFedexCrossBorderCarrierForm
          carrierInput={newCarrier.inputs.fedexCrossBorderCarrierInput}
        />
      );
    case RedoCarrierName.VEHO:
      return (
        <CreateVehoCarrierForm
          carrierInput={newCarrier.inputs.vehoCarrierInput}
        />
      );
    case RedoCarrierName.APC:
      return (
        <CreateApcCarrierForm
          carrierInput={newCarrier.inputs.apcCarrierInput}
        />
      );
    case RedoCarrierName.FIRST_MILE:
      return (
        <CreateFirstMileCarrierForm
          carrierInput={newCarrier.inputs.firstMileCarrierInput}
        />
      );
    case RedoCarrierName.AUSTRALIA_POST:
      return (
        <CreateAustraliaPostCarrierForm
          carrierInput={newCarrier.inputs.australiaPostCarrierInput}
        />
      );
    case RedoCarrierName.CANADA_POST:
      return (
        <CreateCanadaPostCarrierForm
          carrierInput={newCarrier.inputs.canadaPostCarrierInput}
        />
      );
    case RedoCarrierName.ROYAL_MAIL:
      return (
        <CreateRoyalMailCarrierForm
          carrierInput={newCarrier.inputs.royalMailCarrierInput}
        />
      );
    case RedoCarrierName.ROYAL_MAIL_V3:
      return (
        <CreateRoyalMailV3CarrierForm
          carrierInput={newCarrier.inputs.royalMailV3CarrierInput}
        />
      );
    case RedoCarrierName.SEKO_OMNIPARCEL:
      return (
        <CreateSekoOmniParcelCarrierForm
          carrierInput={newCarrier.inputs.sekoOmniParcelCarrierInput}
        />
      );
    case RedoCarrierName.GLS:
      return (
        <CreateGlsCarrierForm
          carrierInput={newCarrier.inputs.glsCarrierInput}
        />
      );
    case RedoCarrierName.ASENDIA:
      return (
        <CreateAsendiaCarrierForm
          carrierInput={newCarrier.inputs.asendiaCarrierInput}
        />
      );
    case RedoCarrierName.SPEE_DEE:
      return (
        <CreateSpeeDeeCarrierForm
          carrierInput={newCarrier.inputs.speeDeeCarrierInput}
        />
      );
    case RedoCarrierName.AMAZON:
      return (
        <CreateAmazonCarrierForm
          carrierInput={newCarrier.inputs.amazonCarrierInput}
        />
      );
    case RedoCarrierName.USPS_SHIP:
      return (
        <CreateUspsShipCarrierForm
          carrierInput={newCarrier.inputs.uspsShipCarrierInput}
        />
      );
    case RedoCarrierName.USPS_DIRECT:
      return (
        <CreateUspsDirectCarrierForm
          carrierInput={newCarrier.inputs.uspsDirectCarrierInput}
        />
      );
    case RedoCarrierName.PASSPORT_GLOBAL:
      return (
        <CreatePassportGlobalCarrierForm
          carrierInput={newCarrier.inputs.passportGlobalCarrierInput}
        />
      );
    case RedoCarrierName.EPOST_GLOBAL:
      return (
        <CreateEPostGlobalCarrierForm
          carrierInput={newCarrier.inputs.epostGlobalCarrierInput}
        />
      );
    case RedoCarrierName.ETA_EXPRESS:
      return (
        <CreateETAExpressCarrierForm
          carrierInput={newCarrier.inputs.etaExpressCarrierInput}
        />
      );
    case RedoCarrierName.ONTRAC:
      return (
        <CreateOnTracCarrierForm
          carrierInput={newCarrier.inputs.onTracCarrierInput}
        />
      );
    default:
      assertNever(newCarrier.inputs.redoCarrierName.value);
  }
};

const carrierCardLabel = (carrier: RedoCarrierName) => {
  switch (carrier) {
    case RedoCarrierName.UPS_MAIL_INNOVATIONS: {
      return "UPS Mail Innovations®";
    }
    case RedoCarrierName.UPS_SUREPOST: {
      return "UPS Ground Saver®";
    }
    case RedoCarrierName.USPS_DIRECT: {
      return "USPS";
    }
    case RedoCarrierName.UPS: {
      return carrierToLabel[redoCarrierNameToCarrier[carrier]] ?? carrier;
    }
    default: {
      return carrier;
    }
  }
};

const CarrierCard = memo(function CarrierCard({
  carrier,
  onClick,
  allowCreate = false,
}: {
  carrier: RedoCarrierName;
  onClick: (carrier: RedoCarrierName) => void;
  allowCreate?: boolean;
}) {
  return (
    <Flex
      bgColor="primary"
      borderColor="primary"
      borderStyle="solid"
      borderWidth="1px"
      cursor="pointer"
      flexDirection="column"
      gap="lg"
      hoverColor="secondary"
      onClick={() => allowCreate && onClick(carrier)}
      p="lg"
      radius="md"
      style={{ width: "200px" }}
      translate="yes"
    >
      <OptionalTooltip
        meetsCondition={!allowCreate}
        tooltipProps={{
          title: "Reach out to your Redo support contact to set up",
        }}
      >
        <Flex dir="column" w="full">
          <Flex align="center" dir="row" gap="lg">
            <ShippingLogo
              carrier={
                isEnumValue(carrier, Carriers)
                  ? redoCarrierNameToCarrier[carrier]
                  : carrier
              }
              size={LogoSize.MEDIUM}
            />
            <Text fontSize="sm" fontWeight="semibold">
              {carrierCardLabel(carrier)}
            </Text>
          </Flex>
          {allowCreate && (
            <Flex justify="flex-end" w="full">
              <ArrowRightIcon style={{ width: "16px", height: "16px" }} />
            </Flex>
          )}
        </Flex>
      </OptionalTooltip>
    </Flex>
  );
});

const AddCarrierForm = memo(function AddCarrierForm({
  newCarrier,
}: {
  newCarrier: CarrierInputForm;
}) {
  return (
    <Flex dir="column" gap="3xl" w="full">
      <RedoTextInput
        label="Account name"
        placeholder="Enter a name"
        setValue={newCarrier.inputs.name.setValue}
        value={newCarrier.inputs.name.value}
      />
      {carrierSpecificForm(newCarrier)}
    </Flex>
  );
});
